import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

// Lazy import to avoid circular dependency
let storeModule: any = null;
const getStore = () => {
  if (!storeModule) {
    storeModule = require('../store');
  }
  return storeModule.store;
};

const getAuthActions = () => {
  return require('../store/slices/authSlice');
};

// Token refresh configuration
const TOKEN_REFRESH_THRESHOLD_MINUTES = 5; // Refresh token 5 minutes before expiry
let tokenRefreshTimer: NodeJS.Timeout | null = null;
let refreshPromise: Promise<string> | null = null;

interface JWTPayload {
  sub: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
}

/**
 * Check if token is about to expire (within threshold)
 */
const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const thresholdMs = TOKEN_REFRESH_THRESHOLD_MINUTES * 60 * 1000;

    return timeUntilExpiry <= thresholdMs;
  } catch {
    return true; // If we can't decode, assume it needs refresh
  }
};

/**
 * Get time until token expires in milliseconds
 */
const getTimeUntilExpiry = (token: string): number => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    const expirationTime = decoded.exp * 1000;
    return expirationTime - Date.now();
  } catch {
    return 0;
  }
};

const refreshAccessTokenOnce = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const store = getStore();
  const state = store.getState();

  if (!state.auth.refreshToken) {
    throw new Error('No refresh token available');
  }

  const { refreshAccessToken } = getAuthActions();

  const inFlightRefresh = store.dispatch(refreshAccessToken()).unwrap()
    .then(() => {
      const nextToken = store.getState().auth.accessToken;
      if (!nextToken) {
        throw new Error('Access token missing after refresh');
      }
      return nextToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  refreshPromise = inFlightRefresh;
  return inFlightRefresh;
};

/**
 * Schedule proactive token refresh before expiry
 */
const scheduleTokenRefresh = (token: string): void => {
  // Clear any existing timer
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }

  const timeUntilExpiry = getTimeUntilExpiry(token);
  const thresholdMs = TOKEN_REFRESH_THRESHOLD_MINUTES * 60 * 1000;

  // Schedule refresh 5 minutes before expiry
  const refreshTime = Math.max(timeUntilExpiry - thresholdMs, 60000); // At least 1 minute

  if (refreshTime > 0 && refreshTime < 24 * 60 * 60 * 1000) { // Max 24 hours
    tokenRefreshTimer = setTimeout(async () => {
      try {
        const store = getStore();
        const state = store.getState();

        if (state.auth.refreshToken) {
          const newToken = await refreshAccessTokenOnce();
          scheduleTokenRefresh(newToken);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Proactive token refresh failed:', error);
        }
      }
    }, refreshTime);
  }
};

/**
 * Initialize token refresh scheduler on app load
 */
export const initializeTokenRefresh = (): void => {
  const store = getStore();
  const state = store.getState();

  if (state.auth.accessToken && state.auth.refreshToken) {
    scheduleTokenRefresh(state.auth.accessToken);
  }
};

/**
 * Clear token refresh timer (call on logout)
 */
export const clearTokenRefreshTimer = (): void => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

// Determine API URL based on current hostname
const isLoopbackHost = (host: string) =>
  ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(host);

const normalizeConfiguredApiUrl = (configuredUrl?: string) => {
  if (!configuredUrl) return '';
  if (typeof window === 'undefined') return configuredUrl.replace(/\/$/, '');

  try {
    const url = new URL(configuredUrl);
    const appHost = window.location.hostname;
    if (isLoopbackHost(url.hostname) && !isLoopbackHost(appHost)) {
      url.hostname = appHost;
      url.protocol = window.location.protocol;
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return configuredUrl.replace(/\/$/, '');
  }
};

const getApiUrl = () => {
  const configuredUrl = normalizeConfiguredApiUrl(process.env.REACT_APP_API_URL);
  if (configuredUrl) {
    return configuredUrl;
  }

  // Use the same hostname as the web app, but with configured port
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = process.env.REACT_APP_API_PORT || '8000';
  return `${protocol}//${hostname}:${port}`;
};

export const API_URL = getApiUrl();

/**
 * Normalizes an image URL from the API.
 * If it's a relative path, prepends the base URL.
 * If it's already absolute (starts with http), returns as is.
 */
export const getImageUrl = (url?: string | null): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  
  // Ensure the relative path doesn't have an extra slash
  const cleanPath = url.startsWith('/') ? url.slice(1) : url;
  return `${API_URL}/${cleanPath}`;
};

// ============================================================
// Request Deduplication & Caching
// Prevents duplicate simultaneous requests to the same endpoint
// and caches GET responses for faster navigation
// ============================================================

const pendingRequests = new Map<string, Promise<any>>();
const responseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL_MS = 30_000; // 30 seconds stale-while-revalidate
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
const UPLOAD_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1`,
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - Add JWT token, check expiry, and deduplicate
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData && config.headers) {
      delete (config.headers as any)['Content-Type'];
      if (!config.timeout || config.timeout <= DEFAULT_REQUEST_TIMEOUT_MS) {
        config.timeout = UPLOAD_REQUEST_TIMEOUT_MS;
      }
    }

    const store = getStore();
    const state = store.getState();
    let token = state.auth.accessToken;

    // Check if token is about to expire and proactively refresh
    if (token && isTokenExpiringSoon(token) && state.auth.refreshToken) {
      if (!config.url?.includes('/auth/')) {
        try {
          token = await refreshAccessTokenOnce();
          scheduleTokenRefresh(token);
        } catch (error) {
          // Continue with existing token, let 401 handler deal with it
        }
      }
    }

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints to avoid infinite loops
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Another request may have refreshed while this old-token response was in flight.
        const latestToken = getStore().getState().auth.accessToken;
        const nextToken = latestToken && originalRequest.headers?.Authorization !== `Bearer ${latestToken}`
          ? latestToken
          : await refreshAccessTokenOnce();
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${nextToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Token refresh failed:', refreshError);
        }
        return Promise.reject(error);
      }
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

// Error handler utility
export const handleApiError = (error: any): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    const isUploadRequest = typeof FormData !== 'undefined' && axiosError.config?.data instanceof FormData;

    if (
      axiosError.code === 'ECONNABORTED' ||
      axiosError.code === 'ETIMEDOUT' ||
      axiosError.message?.toLowerCase().includes('timeout')
    ) {
      return isUploadRequest
        ? 'Upload timed out. Please keep the app open and try again on a stronger connection.'
        : 'Request timed out. Please check your connection and try again.';
    }

    // Network error
    if (!axiosError.response) {
      return isUploadRequest
        ? 'Upload failed due to a network connection issue. Please check your connection and try again.'
        : 'Network error. Please check your connection.';
    }

    const responseData = axiosError.response.data;
    const fieldLabels: Record<string, string> = {
      username: 'Username',
      full_name: 'Full name',
      email: 'Email',
      phone: 'Phone number',
      country: 'Country',
      language_preference: 'Language preference',
      timezone: 'Timezone',
      gender: 'Gender',
    };
    const formatFieldLabel = (field?: string) => {
      if (!field) return 'This field';
      return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const formatValidationError = (validationError: any): string => {
      const loc = Array.isArray(validationError?.loc)
        ? validationError.loc.filter((part: unknown) => part !== 'body').pop()
        : undefined;
      const field = typeof loc === 'string' ? loc : undefined;
      const message = String(validationError?.msg || 'Invalid value').replace(/^Value error,\s*/i, '');
      const lowerMessage = message.toLowerCase();

      if (field === 'username') {
        if (lowerMessage.includes('at least')) return 'Username must be at least 3 characters.';
        if (lowerMessage.includes('at most') || lowerMessage.includes('too long')) return 'Username must be 100 characters or fewer.';
        if (lowerMessage.includes('pattern') || lowerMessage.includes('string should match')) {
          return 'Username can only use letters, numbers, underscores, or hyphens.';
        }
      }

      if (field === 'phone' && lowerMessage.includes('at most')) {
        return 'Phone number must be 20 characters or fewer.';
      }

      if (field === 'country' && (lowerMessage.includes('at least') || lowerMessage.includes('too short'))) {
        return 'Choose a valid country or leave it unselected.';
      }

      return `${formatFieldLabel(field)}: ${message}`;
    };
    const validationErrors = Array.isArray(responseData?.error?.details?.errors)
      ? responseData.error.details.errors
      : Array.isArray(responseData?.error?.details)
        ? responseData.error.details
      : Array.isArray(responseData?.details?.errors)
        ? responseData.details.errors
        : Array.isArray(responseData?.details)
          ? responseData.details
        : Array.isArray(responseData?.detail)
          ? responseData.detail
          : [];

    // Try to extract error message from various formats
    try {
      if (validationErrors.length) {
        return validationErrors.map(formatValidationError).join(' ');
      }

      // Direct message field
      if (responseData?.message && typeof responseData.message === 'string') {
        return responseData.message;
      }

      // Detail field (can be string or object)
      if (responseData?.detail) {
        if (typeof responseData.detail === 'string') {
          return responseData.detail;
        }
        if (typeof responseData.detail === 'object') {
          // Try message field in detail object
          if (responseData.detail.message && typeof responseData.detail.message === 'string') {
            return responseData.detail.message;
          }
          // Try error field in detail object
          if (responseData.detail.error && typeof responseData.detail.error === 'string') {
            return responseData.detail.error;
          }
          // Try to stringify if it's an object
          return JSON.stringify(responseData.detail);
        }
      }

      // Error field - can be string or object { code, message }
      if (responseData?.error) {
        if (typeof responseData.error === 'string') {
          return responseData.error;
        }
        if (typeof responseData.error === 'object') {
          if (responseData.error.message && typeof responseData.error.message === 'string') {
            return responseData.error.message;
          }
          if (responseData.error.code && typeof responseData.error.code === 'string') {
            return responseData.error.code.replace(/_/g, ' ');
          }
        }
      }

      // If responseData is a string
      if (typeof responseData === 'string') {
        return responseData;
      }
    } catch (e) {
      console.error('Error parsing API error:', e);
    }

    // HTTP status errors with better messages
    switch (axiosError.response.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Unauthorized. Please login again.';
      case 403:
        return 'Access forbidden. You do not have permission.';
      case 404:
        return 'Resource not found.';
      case 413:
        return 'File is too large. Please choose a smaller file.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
      default:
        return `Error: ${axiosError.response.status}`;
    }
  }

  // Generic error
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }

  return 'An unexpected error occurred.';
};

export default api;

// Export token refresh utility for use in auth slice
export { scheduleTokenRefresh };

// ============================================================
// Utility: Cached GET request (stale-while-revalidate)
// Use this for data that doesn't need to be real-time fresh
// ============================================================
export const cachedGet = async <T = any>(url: string, params?: any): Promise<T> => {
  const cacheKey = `${url}${params ? JSON.stringify(params) : ''}`;

  // Check cache first
  const cached = responseCache.get(cacheKey);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < CACHE_TTL_MS) {
    // Fresh cache — return immediately
    return cached.data as T;
  }

  // Check for in-flight dedup
  const pending = pendingRequests.get(cacheKey);
  if (pending) {
    return pending as Promise<T>;
  }

  // Make the request
  const promise = api.get(url, { params }).then(res => {
    responseCache.set(cacheKey, { data: res.data, timestamp: Date.now() });
    pendingRequests.delete(cacheKey);
    return res.data;
  }).catch(err => {
    pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);
  return promise as Promise<T>;
};

// Invalidate cache for a specific URL pattern
export const invalidateCache = (urlPattern?: string): void => {
  if (!urlPattern) {
    responseCache.clear();
    return;
  }
  Array.from(responseCache.keys()).forEach(key => {
    if (key.includes(urlPattern)) {
      responseCache.delete(key);
    }
  });
};
